import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { Modal, Button, Group, Text } from "@mantine/core";
import { LabeledDropDownSelect } from "./DropDownSelect";
import AdminOnlyWarning from "./Settings/AdminOnlyWarning";
import {
	getVersionControlSettings,
	setVersionControlSettings,
} from "../actions/tenantActions";

const VERSION_CONTROL_OPTIONS = [
	{
		value: "automatic",
		label: "AUTOMATIC: New Document Versions are created upon each edit (default)",
	},
	{
		value: "manual",
		label: "MANUAL: New Document Versions are only created when a user initiates",
	},
];

function VersionControlSettings({
	versionControlSettingsLoading,
	canEditSettings,
	firmHasManualVersioningEnabled,
	getVersionControlSettings,
	setVersionControlSettings,
}) {
	const [selectedValue, setSelectedValue] = useState(null);
	const [confirmModalOpen, setConfirmModalOpen] = useState(false);

	useEffect(() => {
		getVersionControlSettings();
	}, [getVersionControlSettings]);

	useEffect(() => {
		if (selectedValue === null && !versionControlSettingsLoading) {
			setSelectedValue(
				firmHasManualVersioningEnabled ? "manual" : "automatic"
			);
		}
	}, [
		versionControlSettingsLoading,
		firmHasManualVersioningEnabled,
		selectedValue,
	]);

	const handleDropdownChange = (name, option) => {
		setSelectedValue(option.value);
	};

	const handleApply = () => {
		setConfirmModalOpen(true);
	};

	const handleConfirm = () => {
		const isManual = selectedValue === "manual";
		setVersionControlSettings({ featureValue: isManual })
			.then(() => setConfirmModalOpen(false))
			.catch(() => setConfirmModalOpen(false));
	};

	const handleCancel = () => {
		setSelectedValue(
			firmHasManualVersioningEnabled ? "manual" : "automatic"
		);
	};

	if (versionControlSettingsLoading && selectedValue === null) {
		return null;
	}

	const currentOption = VERSION_CONTROL_OPTIONS.find(
		(o) => o.value === selectedValue
	);
	const savedValue = firmHasManualVersioningEnabled ? "manual" : "automatic";
	const hasChanged = selectedValue !== savedValue;

	return (
		<div style={{ width: 600, paddingTop: 20, paddingLeft: 15 }}>
			<p style={{ marginBottom: 20 }}>
				Choose how you&apos;d like LexWorkplace to manage Document
				Versions. Note that your selection here applies to all users in
				your firm.
			</p>
			<AdminOnlyWarning
				userCanEditSettings={canEditSettings}
				singular={true}
			/>
			<LabeledDropDownSelect
				label="Version Control"
				name="versionControl"
				options={VERSION_CONTROL_OPTIONS}
				value={currentOption || null}
				defaultValue={currentOption || null}
				handler={handleDropdownChange}
				isDisabled={!canEditSettings}
			/>
			{canEditSettings && hasChanged ? (
				<div style={{ marginTop: 20, display: "flex", gap: 8 }}>
					<Button onClick={handleApply}>Apply</Button>
					<Button variant="default" onClick={handleCancel}>
						Cancel
					</Button>
				</div>
			) : null}
			<Modal
				opened={confirmModalOpen}
				onClose={() => setConfirmModalOpen(false)}
				title="Confirm Version Control Change"
				size="md"
			>
				<Text style={{ marginBottom: 20 }}>
					You are about to change the Version Control for all
					Documents, for all Users in your firm. Please confirm this
					is your intent.
				</Text>
				<Group justify="flex-end">
					<Button
						variant="default"
						onClick={() => setConfirmModalOpen(false)}
					>
						Cancel
					</Button>
					<Button onClick={handleConfirm}>Confirm</Button>
				</Group>
			</Modal>
		</div>
	);
}

const mapStateToProps = (state) => ({
	versionControlSettingsLoading: state.tenant.versionControlSettingsLoading,
	firmHasManualVersioningEnabled:
		state.tenant.tenantFeatures.manualVersioning,
	canEditSettings: state.user.userRights
		? state.user.userRights.UPDATE_VERSION_CONTROL_SETTINGS
		: false,
});

export default connect(mapStateToProps, {
	getVersionControlSettings,
	setVersionControlSettings,
})(VersionControlSettings);
